import { getSessionId } from '@onefootprint/dev-tools';
import { IcoInfo16 } from '@onefootprint/icons';
import type { ApiError } from '@onefootprint/request-types';
import { Button, CodeInline, PageShell } from '@onefootprint/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useLocation, useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/idv2/features/app-shell';
import { useAppNavigate } from '@/idv2/hooks/use-app-navigate';
import { getLogger } from '@/idv2/utils/logger';
import { suppressNextStuckDetection } from '@/idv2/utils/route-tracking';
import { UnsupportedRequirementError } from '@/idv2/utils/unsupported-requirement-error';
import { type ErrorKey, getErrorKey } from './get-error-key';
const {
  logError,
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'route-error-boundary'
});
type ErrorMap = Record<ErrorKey, {
  title: string;
  subtitle: string;
  type: ErrorKey;
}>;

// onboardingSuperseded is a race between two concurrent onboarding sessions, not a server fault.
// playbookDisabled means the company turned the playbook off, so only they can fix it.
// sessionNotFound, like sessionExpired, is a stale or mistyped link the user can't recover from.
const ERROR_TYPES_TO_IGNORE: ErrorKey[] = ['connectionError', 'sessionNotFound', 'sessionExpired', 'businessNotOwnedByUser', 'onboardingSuperseded', 'playbookDisabled'];

/**
 * The root route's errorComponent. The AppShell is here because a superseded session throws from
 * RootComponent itself, so this stands in for the shell rather than rendering inside it.
 */
export const RouteErrorBoundary = ({
  error
}: ErrorComponentProps) => {
  const {
    t
  } = useTranslation('idv2', {
    keyPrefix: 'errorFallback'
  });
  const location = useLocation();
  const {
    token
  } = useParams({
    strict: false
  });
  const {
    scope
  } = useRouteContext({
    strict: false
  });
  const sessionId = getSessionId();
  const {
    forward,
    close
  } = useAppNavigate();
  const errorKey = getErrorKey(error);
  const queryClient = useQueryClient();
  const [isRetrying, setIsRetrying] = useState(false);
  const errorMap: ErrorMap = {
    connectionError: {
      title: t($ => $.connectionError.title),
      subtitle: t($_0 => $_0.connectionError.subtitle),
      type: 'connectionError'
    },
    genericError: {
      title: t($_1 => $_1.genericError.title),
      subtitle: t($_2 => $_2.genericError.subtitle),
      type: 'genericError'
    },
    sessionNotFound: {
      title: t($_3 => $_3.sessionNotFound.title),
      subtitle: t($_4 => $_4.sessionNotFound.subtitle),
      type: 'sessionNotFound'
    },
    sessionExpired: {
      title: t($_5 => $_5.sessionExpired.title),
      subtitle: t($_6 => $_6.sessionExpired.subtitle),
      type: 'sessionExpired'
    },
    sessionInvalid: {
      title: t($_7 => $_7.sessionInvalid.title),
      subtitle: t($_8 => $_8.sessionInvalid.subtitle),
      type: 'sessionInvalid'
    },
    businessNotOwnedByUser: {
      title: t($_9 => $_9.businessNotOwnedByUser.title),
      subtitle: t($_10 => $_10.businessNotOwnedByUser.subtitle),
      type: 'businessNotOwnedByUser'
    },
    onboardingSuperseded: {
      title: t($_11 => $_11.onboardingSuperseded.title),
      subtitle: t($_12 => $_12.onboardingSuperseded.subtitle),
      type: 'onboardingSuperseded'
    },
    playbookDisabled: {
      title: t($_13 => $_13.playbookDisabled.title),
      subtitle: t($_14 => $_14.playbookDisabled.subtitle),
      type: 'playbookDisabled'
    },
    unsupportedRequirement: {
      title: t($_15 => $_15.unsupportedRequirement.title),
      subtitle: t($_16 => $_16.unsupportedRequirement.subtitle),
      type: 'unsupportedRequirement'
    }
  };
  const {
    title,
    type
  } = errorMap[errorKey];
  // Auth scope starts from a login-methods token the company issued, so only the company can issue a new one.
  const isStaleAuthSession = scope === 'auth' && (type === 'sessionNotFound' || type === 'sessionExpired');
  const subtitle = isStaleAuthSession ? t($_17 => $_17.authSessionEndedSubtitle) : errorMap[errorKey].subtitle;
  useEffect(() => {
    // The hosted client throws the response body unconverted, so the support id keeps its snake_case key.
    const apiError = (error && typeof error === 'object' ? error : {}) as Partial<ApiError> & {
      routerCode?: string;
      support_id?: string;
    };
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const unsupportedRequirement = error instanceof UnsupportedRequirementError ? error : undefined;
    const diagnostics = {
      route: token ? location.pathname.replace(token, '$token') : location.pathname,
      type,
      errorSupportId: apiError.support_id,
      routerCode: apiError.routerCode,
      isIframed: window.self !== window.top,
      ancestorOrigin: window.location.ancestorOrigins?.[0],
      navigationType: navigationEntry?.type,
      requirementKind: unsupportedRequirement?.requirementKind,
      orgId: unsupportedRequirement?.orgId
    };
    if (!ERROR_TYPES_TO_IGNORE.includes(type)) {
      logError(`Route error boundary caught ${type}:`, error, diagnostics);
    } else {
      logWarn(`Route error boundary caught ${type}:`, error, diagnostics);
    }
  }, [error, location.pathname, token, type]);
  const onRetry = async () => {
    logInfo('User clicked retry button', {
      error: error?.message ?? 'Unknown error'
    });
    setIsRetrying(true);
    // removeQueries() deletes cached state synchronously, forcing the next
    // ensureQueryData in beforeLoad to re-run its queryFn. resetQueries races a
    // background refetch with the navigation, which can leave a stale error in place.
    queryClient.removeQueries();
    // A retry asks for the step that failed to load, so landing on it again is not a stuck user.
    suppressNextStuckDetection();
    try {
      await forward();
    } finally {
      setIsRetrying(false);
    }
  };
  const handleClose = () => {
    logInfo('User clicked close button', {
      error: error?.message ?? 'Unknown error'
    });
    close?.();
  };
  return <AppShell showCloseButton isErrorScreen>
      <PageShell title={title} subtitle={subtitle}>
        <div className="flex flex-col items-center gap-4 p-6 mt-4 border border-tertiary rounded-md">
          <div className="flex items-center gap-1">
            <IcoInfo16 color="primary" />
            <span className="text-label-2 text-primary">{t($_18 => $_18.sessionId)}</span>
          </div>
          <span className="text-body-2 text-center text-primary">{t($_19 => $_19.sessionIdHint)}</span>
          <CodeInline>{sessionId}</CodeInline>
        </div>
        {type === 'connectionError' || type === 'genericError' ? <Button onClick={onRetry} loading={isRetrying} fullWidth size="large" className="mt-6">
            {t($_20 => $_20.tryAgain)}
          </Button> : <Button onClick={handleClose} fullWidth size="large" className="mt-6">
            {t($_21 => $_21.close)}
          </Button>}
      </PageShell>
    </AppShell>;
};